import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Company } from '../../services/coreCompaniesApi';

type CompanyAutocompleteProps = {
  companies: Company[];
  companyId?: string;
  disabled?: boolean;
  canCreate?: boolean;
  onSelect: (company: Company | null) => void;
  onCreate: () => void;
};

export function CompanyAutocomplete({
  companies,
  companyId,
  disabled = false,
  canCreate = false,
  onSelect,
  onCreate,
}: CompanyAutocompleteProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = companies.find((company) => company.id === companyId);

  useEffect(() => {
    setQuery(selected?.name ?? '');
  }, [selected?.id, selected?.name]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery(selected?.name ?? '');
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [selected?.name]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    const selectedName = selected?.name ?? '';
    if (!term || term === selectedName.toLowerCase()) {
      return companies;
    }

    return companies.filter((company) => company.name.toLowerCase().includes(term));
  }, [companies, query, selected?.name]);

  function choose(company: Company): void {
    onSelect(company);
    setQuery(company.name);
    setOpen(false);
  }

  function clear(): void {
    onSelect(null);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative">
      <input
        className="erp-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={query}
        placeholder="Busque pelo nome da empresa"
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          if (!event.target.value.trim()) {
            onSelect(null);
          }
        }}
      />
      {open && !disabled ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-secondary-200 bg-white py-1 shadow-lg"
        >
          {canCreate ? (
            <li>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-primary-700 hover:bg-primary-50"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setOpen(false);
                  onCreate();
                }}
              >
                <PlusIcon className="h-4 w-4" aria-hidden />
                Criar nova empresa
              </button>
            </li>
          ) : null}
          {companyId ? (
            <li>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-secondary-500 hover:bg-secondary-50"
                onMouseDown={(event) => event.preventDefault()}
                onClick={clear}
              >
                Sem empresa
              </button>
            </li>
          ) : null}
          {visible.length === 0 ? (
            <li className="px-3 py-2 text-sm text-secondary-500">Nenhuma empresa encontrada.</li>
          ) : (
            visible.map((company) => (
              <li key={company.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={company.id === companyId}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-secondary-50 ${
                    company.id === companyId ? 'bg-primary-50 text-primary-800' : 'text-secondary-800'
                  }`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(company)}
                >
                  {company.name}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
